import React, { useState } from "react";
import * as XLSX from "xlsx";
import  { sendUploadFile }  from "../../apiHooks/mutations/sendUpload/SendUpload";
import { CustomButton } from "../buttons/customButton";
import saveIcon from "../../assets/icons/saveIcon.svg";
import warningIcon from "../../assets/icons/importWarning.svg";

interface ModalProps {
    columns: string[];
    previewData: string[][];
    mappedColumns: { [key: number]: string };
    setMappedColumns: (mapped: any) => void;
    closeModal: () => void;
    originalFileName: string;
}

const acceptedMappings = [
    "Tracking Source Name", "Campaign Name", "Ad Group Name", "Ad Name",
    "Campaign Code", "Conversion Value", "Ad Group Code", "Ad Code",
    "Description"
];

const ImportModal: React.FC<ModalProps> = (props) => {
    const [fileName, setFileName] = useState(props.originalFileName.replace(/\.[^/.]+$/, ""));
    const [showFileNameModal, setShowFileNameModal] = useState(false);

    const { mutate: uploadFile, isLoading } = sendUploadFile({
        onSuccess: () => {
            props.closeModal();
        },
    });

    const handleMappingChange = (colIndex: number, value: string) => {
        props.setMappedColumns((prev: any) => ({ ...prev, [colIndex]: value }));
    };

    const handleOpenFileNameModal = () => {
        setShowFileNameModal(true);
    };

    // const handleSaveFile = () => {
    //     const selectedIndexes = Object.keys(mappedColumns).map(Number);
    //     const mappedHeaders = selectedIndexes.map((index) => mappedColumns[index]);
    //     const mappedData = selectedIndexes.map((index) => [previewData[0][index], ...previewData.map((row) => row[index] || "")]);
    //
    //     const finalData = [mappedHeaders, ...mappedData[0].map((_, i) => mappedData.map(row => row[i]))];
    //
    //     const worksheet = XLSX.utils.aoa_to_sheet(finalData);
    //     const workbook = XLSX.utils.book_new();
    //     XLSX.utils.book_append_sheet(workbook, worksheet, "Mapped Data");
    //
    //     const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    //     const file = new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    //
    //     const link = document.createElement("a");
    //     link.href = URL.createObjectURL(file);
    //     link.download = `${fileName}.xlsx`;
    //     document.body.appendChild(link);
    //     link.click();
    //     document.body.removeChild(link);
    // };

    const handleUpload = () => {
        const selectedIndexes = Object.keys(props.mappedColumns).map(Number);

        const mappingObject = selectedIndexes.reduce((acc, index) => {
            const selectedValue = props.mappedColumns[index];
            const columnHeader = props.columns[index];
            if (selectedValue) {
                acc[selectedValue] = columnHeader;
            }
            return acc;
        }, {} as Record<string, string>);

        const mappedHeaders = selectedIndexes.map((index) => props.mappedColumns[index]);
        const mappedData = selectedIndexes.map((index) => [props.previewData[0][index], ...props.previewData.map((row) => row[index] || "")]);

        const finalData = [mappedHeaders, ...mappedData[0].map((_, i) => mappedData.map(row => row[i]))];

        const worksheet = XLSX.utils.aoa_to_sheet(finalData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Mapped Data");

        const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const file = new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const excelFile = new File([file], `${fileName}.xlsx`, { type: file.type });

        const formData = new FormData();
        formData.append("file", excelFile);
        formData.append("upload_type", "mapping");
        formData.append("mappings", JSON.stringify(mappingObject));

        uploadFile(formData);
    };



    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white flex flex-col justify-between h-[450px] rounded-lg shadow-lg w-[850px] max-w-6xl p-4">
                <h2 className="text-base font-medium mb-4 text-center border-b border-b-[#E0E0E0] pb-4">
                    Import Data Into Table
                </h2>
                <div>
                    <div className="overflow-x-auto pb-8 border rounded">
                        <table className="min-w-full rounded-[8px] ">
                            <thead>
                            <tr>
                                {props.columns.map((col, index) => (
                                    <th key={index} className="border border-[#E0E0E0] text-start">
                                        <div className="flex flex-col items-center w-[192px]">
                                            <select
                                                className={`border pl-1 py-2 shadow-none rounded my-4 w-[120px] text-xs font-medium hover:cursor-pointer
                                                ${props.mappedColumns[index] ? "bg-[#9EB6BF80] border-[#6D919E]" : "bg-white border-[#CEDADF]"}`}
                                                onChange={(e) => handleMappingChange(index, e.target.value)}
                                                value={props.mappedColumns[index] || ""}
                                            >
                                                <option className="w-[200px] text-center hover:cursor-pointer" value="">Map Data</option>
                                                {acceptedMappings.map((option, i) => (
                                                    <option className="w-[200px] text-center hover:cursor-pointer" key={i} value={option} disabled={Object.values(props.mappedColumns).includes(option)}>
                                                        {option}
                                                    </option>
                                                ))}
                                            </select>
                                            <span className={`w-48 text-xs font-medium py-2 pl-3 text-start ${
                                                props.mappedColumns[index] ? "bg-[#9EB6BF]" : "bg-[#CEDADF80]"
                                            }`}>{col}</span>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                            </thead>
                            <tbody>
                            {props.previewData.slice(0, 3).map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {row.map((cell, colIndex) => (
                                        <td
                                            key={colIndex}
                                            className={`border border-[#E0E0E0] px-3 py-2 text-start ${
                                                props.mappedColumns[colIndex] ? "bg-[#CEDADF80]" : ""
                                            }`}
                                        >
                                            {cell || "-"}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex items-center px-1 mt-2 gap-2 text-[#DB3A34] text-xs">
                        <img src={warningIcon} alt="" />
                        <p>Message Content</p>
                    </div>
                </div>

                <div className="flex justify-end mt-4 px-1 py-4">
                    {/*<CustomButton variant="outline" onClick={handleOpenFileNameModal} className="border px-4 py-2 rounded mr-2">*/}
                    {/*    Save <img src={saveIcon} alt="" />*/}
                    {/*</CustomButton>*/}
                    <div className="flex items-center gap-2">
                        <CustomButton variant={"noOutline"} onClick={props.closeModal}>
                            Cancel
                        </CustomButton>
                        <CustomButton onClick={handleUpload} disabled={isLoading}>
                            {isLoading ? "Uploading..." : "Upload"}
                        </CustomButton>
                    </div>
                </div>
            </div>

            {showFileNameModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg w-[400px]">
                        <h3 className="text-base font-normal mb-4 text-center">Save File Name</h3>
                        <input
                            type="text"
                            value={fileName}
                            onChange={(e) => setFileName(e.target.value)}
                            className="border border-[#E0E0E0] p-2 w-full rounded outline-none"
                            placeholder="Enter file name"
                        />
                        <div className="flex justify-end gap-2 mt-4">
                            <CustomButton variant="noOutline" onClick={() => setShowFileNameModal(false)}>
                                Cancel
                            </CustomButton>
                            {/*<CustomButton onClick={handleSaveFile}>Save</CustomButton>*/}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImportModal;
